/**
 * auth-api error messages for the email verification code flow (SKY-4184).
 *
 * auth-api returns no machine-readable error code for these — only an HTTP
 * status and this (i18n) `message` — so callers match on both. Keep every
 * consumer on these constants so a backend rewording is a one-line change.
 */
export const EMAIL_CONFIRMATION_ERROR_MESSAGES = {
  invalidCode: 'Email verification code is invalid or expired, please request a new one',
  attemptsExhausted: 'Too many attempts, please request a new verification code',
  verifyRateLimited: 'Too many verification attempts, please try again later',
  resendTooSoon: 'Please wait a minute before requesting a new verification code',
  alreadyConfirmed: 'Email already confirmed',
} as const;

/**
 * `fixedCacheKey` of the token refresh the signup listener runs after the email
 * is confirmed. A component can pass it to `useRefreshToken` to observe that
 * refresh and retry it if it fails.
 */
export const EMAIL_CONFIRMATION_REFRESH_KEY = 'email-confirmation-refresh';

/**
 * auth-api error messages of the password reset flow (SKY-4829,
 * `reset-password.exceptions.*`).
 *
 * As with email confirmation, auth-api's exception filter returns only the HTTP
 * status and this (i18n) `message`, not the exception code, so callers match on
 * both. Several codes share a status: `invalidCode`, `attemptsExhausted` and
 * `invalidMfaCode` are all 400; `verifyRateLimited` and `resendTooSoon` are both
 * 429; `emailNotVerified` and `mfaRequired` are both 403.
 */
export const RESET_PASSWORD_ERROR_MESSAGES = {
  invalidCode: 'Password reset code is invalid or expired, please request a new one',
  attemptsExhausted: 'Too many attempts, please request a new password reset',
  verifyRateLimited: 'Too many password reset attempts, please try again later',
  resendTooSoon: 'Please wait before requesting a new password reset',
  sessionExpired: 'Password reset session expired, please request a new password reset',
  emailNotVerified: 'Please enter the code from the email first',
  mfaRequired: 'Please pass 2FA first',
  invalidMfaCode: 'Invalid 2FA code',
} as const;
